import { useHookstate } from "@hookstate/core";
import { Box, Card, CardContent, Chip, CircularProgress, Grid, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { getIncidentById } from "~/features/Map/services/request";
import useMapState, { useMarkerState } from "~/features/Map/stores/MapState";
import type { IncidentResponse } from "~/features/Map/types";
import { useLang } from "~/hooks/useLang";
import Close from '@mui/icons-material/Close'
import Pin from "@mui/icons-material/Pin";
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import PersonIcon from '@mui/icons-material/Person'
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import dayjs from "dayjs";
import { dateTimeFormat, elapsedTimeInMinutes } from "~/utils/dateTime";
import { elapsedTime } from "~/features/Dispatch/components/DispatchFormModalDraggable";
import useSystemState from "~/features/System/stores/SystemState";
import DialogOpenDispatchPopupLocationRequester from "./components/DialogOpenDispachPopupLocationRequester";
import { formatPhone } from "~/utils/format";
import RadarIcon from '@mui/icons-material/Radar';

// eslint-disable-next-line max-lines-per-function
export default function MarkerPopupContentRequesterLocation({ incidentId, closeThisPopUp }) {
  const { translate } = useLang();
  const { getStatusColors } = useSystemState()
  const { forceClosePopup } = useMarkerState();
  const { markers } = useMapState();
  const existMarker = useHookstate<string[]>([]);
  const openDialog = useHookstate<boolean>(false);
  const stationHasAccess = useHookstate<boolean>(true);
  const dispatchCode = useHookstate<string | null>(null);
  const dispatchId = useHookstate<number | null>(null);
  const incidentLocation = useHookstate<IncidentResponse | null>(null);
  const loading = useHookstate(false);

  useEffect(() => {
    existMarker.set(Object.keys(markers()['locationRequester'].get()).filter((item) => item == String(incidentId)))
    if (!existMarker.get().length) {
      closeThisPopUp()
    }
  }, [forceClosePopup.get()])

  useEffect(() => {
    loading.set(true);
    getIncidentById({ incidentId: String(incidentId) })
      .then((response) => {
        incidentLocation.set(response);
      }).finally(() => {
        loading.set(false);
      })
  }, [incidentId]);

  const handleClickOpen = (dispatch) => {
    dispatchId.set(dispatch.id);
    dispatchCode.set(dispatch.code);
    stationHasAccess.set(dispatch.stationHasAccess);
    openDialog.set(true);
  }

  const removeCountryCode = (phoneString: string): string => {
    if (!phoneString) return '';
    return phoneString.replace(/^\+55\s?/, '');
  }

  return (
    <>
      {loading.get() ?
        <Card sx={{
          height: '400px',
          backgroundColor: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <CircularProgress size={24} />
        </Card> :
        <Card
          sx={{
            backgroundColor: 'transparent',
            backgroundImage: 'none',
            boxShadow: 'none',
          }}
        >
          <CardContent sx={{ padding: '1em 0 1em 0' }}>
            <Stack direction="row" alignItems="center" gap={2} justifyContent={'space-between'}>
              <Tooltip title={incidentLocation.get()?.is_driver ? translate('Driver') : translate('Passenger')} placement='left'>
                <Box display="flex" flexDirection={'row'} alignItems="center" justifyContent={'flex-start'}>
                  <PersonIcon sx={{ mr: '10px' }} />
                  <Typography
                    className="popupText-dark"
                    variant="subtitle1"
                    sx={{ margin: '.6em 0' }}
                  >
                    {incidentLocation.get()?.is_driver ? translate('Driver') : translate('Passenger')}
                  </Typography>
                </Box>
              </Tooltip>
              <IconButton size="small" onClick={() => closeThisPopUp()}>
                <Close fontSize="small" />
              </IconButton>
            </Stack>

            <Tooltip title={translate('Requester')} placement='left'>
              <Stack direction='row' alignItems='center'>
                <ContactEmergencyIcon sx={{ mr: '10px' }} />
                <Typography variant='subtitle1' sx={{ margin: '.4em 0' }}>
                  {`${incidentLocation.get()?.user_name}`}
                </Typography>
              </Stack>
            </Tooltip>

            <Tooltip title={translate('Phone')} placement='left'>
              <Stack direction='row' alignItems='center'>
                <ContactPhoneIcon sx={{ mr: '10px' }} />
                <Typography variant='subtitle1' sx={{ margin: '.4em 0' }}>
                  {`${formatPhone(removeCountryCode(String(incidentLocation.get()?.phone)))}`}
                </Typography>
              </Stack>
            </Tooltip>

            <Tooltip title={translate('Plate')} placement='left'>
              <Stack direction='row' alignItems='center'>
                <Pin sx={{ mr: '10px' }} />
                <Typography variant='subtitle1' sx={{ margin: '.4em 0' }}>
                  {incidentLocation.get()?.plate}
                </Typography>
              </Stack>
            </Tooltip>

            <Stack direction='row' alignItems='center' >
              <Tooltip title={translate('Last update')} placement='left'>
                <AccessTimeIcon sx={{ mr: '10px' }} />
              </Tooltip>
              <Typography variant='subtitle1' sx={{ margin: '.4em 0' }} >
                {dayjs(incidentLocation.get()?.updated_at).isValid() ? dayjs(incidentLocation.get()?.locations[0].created_at).format('DD/MM/YYYY HH:mm:ss') : ''}
              </Typography>
              <Tooltip title={`Localização enviada em: ${dateTimeFormat(incidentLocation.get()?.locations[0].created_at.toString())}`} placement='right'>
                <Chip sx={{ ml: '5px' }} variant={'outlined'} label={elapsedTime(incidentLocation.get()?.locations[0].created_at.toString())} />
              </Tooltip>
            </Stack>

            <Tooltip title=
              {`
              ${incidentLocation.get()?.vehicle_brand ? `${translate('Brand')}, ` : null}
              ${incidentLocation.get()?.vehicle_model ? `${translate('Model')}, ` : null}
              ${incidentLocation.get()?.vehicle_color ? `${translate('Color')}` : null}
            `}
              placement='left'>
              <Stack direction='row' alignItems='center'>
                <DriveEtaIcon sx={{ mr: '10px' }} />
                <Typography variant='subtitle1' sx={{ margin: '.4em 0' }}>
                  {`${incidentLocation.get()?.vehicle_brand ? incidentLocation.get()?.vehicle_brand : null}, `}
                  {`${incidentLocation.get()?.vehicle_model ? incidentLocation.get()?.vehicle_model : null}, `}
                  {`${incidentLocation.get()?.vehicle_color ? incidentLocation.get()?.vehicle_color : null}`}

                </Typography>
              </Stack>
            </Tooltip>
            {incidentLocation.get()?.locations[0]?.address &&
              <Tooltip
                title={`${Boolean(incidentLocation.get()?.active)
                  ? translate('Address')
                  : translate('Trip completed on the platform, last location received') }`}
                placement='left'
              >
                <Stack direction='row' alignItems='center'>
                  <RadarIcon sx={{ mr: '10px', color: Boolean(incidentLocation.get()?.active) ? '#000000DE' : 'red' }} />
                  <Typography variant='subtitle1' sx={{ margin: '.4em 0', color: Boolean(incidentLocation.get()?.active) ? '#000000DE' : 'red' }} >
                    {incidentLocation.get()?.locations[0]?.address}
                  </Typography>
                </Stack>
              </Tooltip>
            }
            <Stack direction="row" alignItems="center" justifyContent={'center'} gap={1} marginTop={2} sx={{ flexWrap: "wrap" }}>
              {
                incidentLocation.get()?.occurrences?.map((occurrence) => {
                  return occurrence.dispatches.map((dispatch) => {
                    return (
                      <Chip
                        key={dispatch.id}
                        label={dispatch.code}
                        onClick={() => handleClickOpen(dispatch)}
                        sx={{
                          backgroundColor: getStatusColors(dispatch.status.id).main,
                          color: getStatusColors(dispatch.status.id).contrastText,
                          '&:hover': {
                            backgroundColor: getStatusColors(dispatch.status.id).dark,
                            color: getStatusColors(dispatch.status.id).contrastText,
                          },
                        }}
                      />
                    )
                  })
                })
              }
            </Stack>
          </CardContent>
        </Card>}
      <DialogOpenDispatchPopupLocationRequester
        open={openDialog}
        stationHasAccess={stationHasAccess}
        dispatchCode={dispatchCode.get()}
        dispatchId={dispatchId.get()}
      />
    </>
  )
}